import { TraitGroup } from '@design-system/trait-group';
import { Typography } from '@design-system/typography';
import { VariantSelector } from '@design-system/variant-selector';
import { WhereToBuy } from '@design-system/where-to-buy';
import React, { FC, forwardRef, lazy } from 'react';

import { ElementOnScreen } from '~/components/ElementOnScreen';
import { accordionMapper } from '~/mappers/accordion';
import { countryPickerMapper } from '~/mappers/countryPicker';
import { htmlRTEBlockMapper } from '~/mappers/htmlRTEBlock';
import { iframeMapper } from '~/mappers/iframe';
import { Image } from '~/mappers/image/models';
import { imageBlockMapper } from '~/mappers/imageBlock';
import { imagePlusTextBlockMapper } from '~/mappers/imagePlusTextBlock';
import { imageTextBlockMapper } from '~/mappers/imageTextBlock';
import { itemListingMapper } from '~/mappers/itemListing';
import { jsonRTEBlockMapper } from '~/mappers/jsonRTEBlock';
import { ldsBlockMapper } from '~/mappers/ldsBlock';
import { linksListMapper } from '~/mappers/linksList';
import { LoginRegisterBlocksMapper } from '~/mappers/LoginRegisterBlocks';
import { mediaPlusTextBlockMapper } from '~/mappers/mediaPlusTextBlock';
import { pimAssetsCarouselMapper } from '~/mappers/pimAssetsCarousel';
import { registeredWrapperMapper } from '~/mappers/registeredWrapper';
import { searchResultsMapper } from '~/mappers/searchResults';
import { sitemapMapper } from '~/mappers/sitemap';
import { tabsMapper } from '~/mappers/tabs';
import { traitGroupMapper } from '~/mappers/traitGroup';
import { traitGroupPIMMapper } from '~/mappers/traitGroupPIM';
import { typographyMapper } from '~/mappers/typography';
import { variantSelectorMapper } from '~/mappers/variantSelector';
import { whereToBuyMapper } from '~/mappers/whereToBuy';

import { BodyRenderProps } from './models';

const AccordionList = lazy(() => import('~/components/AccordionList'));
const DebugComponent = lazy(
  () => import('~/components/BodyRender/DebugComponent')
);
const Iframe = lazy(() => import('~/components/Iframe'));
const ImagePlusTextBlock = lazy(
  () => import('~/components/ImagePlusTextBlock')
);
const ImageTextBlock = lazy(() => import('~/components/ImageTextBlock'));
const ItemListing = lazy(() => import('~/components/ItemListing'));
const LdsBlock = lazy(() => import('~/components/LdsBlock'));
const LoginRegisterBlocks = lazy(
  () => import('~/components/LoginRegisterBlocks')
);
const MediaPlusTextBlock = lazy(
  () => import('~/components/MediaPlusTextBlock')
);
const SearchResults = lazy(() => import('~/components/SearchResults'));
const SitemapList = lazy(() => import('~/components/SitemapList'));
const TabsBlock = lazy(() => import('~/components/TabsBlock'));

const ImageBlock = lazy(() => import('~/components/ImageBlock'));
const JsonRTEBlock = lazy(() => import('~/components/JsonRTEBlock'));
const LinksList = lazy(() => import('~/components/LinksList'));
const PIMAssetsCarousel = lazy(() => import('~/components/PIMAssetsCarousel'));
const CountryPickerBlock = lazy(
  () => import('~/components/CountryPickerBlock')
);
const HtmlRTEBlock = lazy(() => import('~/components/HtmlRTEBlock'));
const RegisteredWrapper = lazy(() => import('~/components/RegisteredWrapper'));
export const typesMapper = {
  default: (typeName) => [
    forwardRef<HTMLDivElement, object>((item, ref) =>
      process.env.NODE_ENV === 'development' ? (
        <DebugComponent typeName={typeName} {...item} ref={ref} />
      ) : null
    ),
    (item) => item,
  ],
  ItemListing: [ItemListing, itemListingMapper],
  AllItemListingGrid: [ItemListing, itemListingMapper],
  ImageTextBlock: [
    ImageTextBlock,
    imageTextBlockMapper,
    ({ items, componentId, pageBlockUI }) => ({
      items,
      componentId,
      pageBlockUI,
    }),
  ],
  TextBlock: [Typography, ({ typography }) => typographyMapper(typography)],
  Typography: [Typography, typographyMapper],
  Image: [ImageBlock, (image: Image) => imageBlockMapper({ images: [image] })],
  ImageBlock: [ImageBlock, imageBlockMapper],
  ImagePlusTextBlock: [ImagePlusTextBlock, imagePlusTextBlockMapper],
  MediaPlusTextBlock: [MediaPlusTextBlock, mediaPlusTextBlockMapper],
  LdsBlock: [LdsBlock, ldsBlockMapper],
  SitemapBlock: [SitemapList, sitemapMapper],
  Iframe: [Iframe, iframeMapper],
  SearchResults: [SearchResults, searchResultsMapper],
  Accordion: [AccordionList, accordionMapper],
  LoginRegister: [LoginRegisterBlocks, LoginRegisterBlocksMapper],
  Tabs: [TabsBlock, tabsMapper],
  WhereToBuy: [WhereToBuy, whereToBuyMapper],
  VariantSelector: [VariantSelector, variantSelectorMapper],
  JsonRTEBlock: [
    JsonRTEBlock,
    jsonRTEBlockMapper,
    ({ rte, componentId, pageBlockUI, showLoadMore, cta }) => ({
      rte,
      componentId,
      pageBlockUI,
      showLoadMore, cta
    }),
  ],
  TraitGroup: [TraitGroup, traitGroupMapper],
  LinksList: [
    LinksList,
    linksListMapper,
    ({ pageBlockUI, componentId, variant, size, items }) => ({
      pageBlockUI,
      componentId,
      variant,
      size,
      items,
    }),
  ],
  PIMAssetsCarousel: [
    PIMAssetsCarousel,
    pimAssetsCarouselMapper,
    (props) => props,
  ],
  TraitGroupPIM: [TraitGroup, traitGroupPIMMapper],
  CountryPicker: [CountryPickerBlock, countryPickerMapper],
  HtmlRTEBlock: [
    HtmlRTEBlock,
    htmlRTEBlockMapper,
    ({ rte, componentId, pageBlockUI }) => ({ rte, componentId, pageBlockUI }),
  ],
  RegisteredWrapper: [RegisteredWrapper, registeredWrapperMapper],
};

const BodyRender: FC<BodyRenderProps> = ({
  items,
  currentPageUid,
  externalData,
}) => {
  const renderedItems = items.map((item) => {
    const value = Object.values(item).find((val) => val);

    return value || { typeName: 'Unknown component', item: [] };
  });

  return renderedItems?.map((allValues, index) => {
    const { typeName } = allValues;
    const { item } = allValues;

    const [ItemComponent, itemMapper, customValue] =
      typesMapper[typeName] || typesMapper.default(typeName);

    const key = `${typeName}__${index}`;
    let value = Array.isArray(item) ? item[0] : item;

    if (customValue) {
      value = customValue(allValues);
    }

    if (typeName === 'Tabs') {
      value = { ...(value as {}), currentPageUid };
    }

    return (
      <ElementOnScreen
        key={key}
        componentName={typeName}
        render={(ref) => (
          <ItemComponent
            ref={ref}
            key={key}
            {...(itemMapper
              ? itemMapper(value, currentPageUid, externalData)
              : value)}
          />
        )}
      />
    );
  });
};

export default BodyRender;
